/**
 * Dashboard entry point.
 *
 * Bootstraps the session manager (which creates the Auth0 SPA client up front so
 * the external-site login flow + callback handling are ready), the shared
 * `ApiClient` (token pulled live from the manager) and the TanStack Query
 * client, starts the background hydration of the effective chain registry,
 * then mounts the provider stack + router. Auth0 callback restoration is handled inside `<SessionProvider>` /
 * `<RequireSession>` — no callback route.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { AppProviders } from "./app/providers";
import { unwrapData, type ApiClient } from "@authe/fe-commons/api";
import { startEffectiveChainsHydration } from "@authe/fe-commons/chains";
import { ImpersonationEndedError, createSessionManager, leaveEndedImpersonation } from "./app/session";
import { createApiClient } from "./app/api-client";
import { getRoutingToken, setRoutingToken, trackBearer } from "./app/routing-token";
import { configureCdnForApp } from "./app/cdn";
import { configureDeviceSigningForApp } from "./app/device-signing";
import { createDashboardGraphqlClient } from "./app/graphql";
import { createQueryClient } from "./config";
import { configureDisplayDefaults } from "./config/display";
import { appEnv } from "./config/env";
import { initRum } from "./app/observability";
import { router } from "./app/router";
import { applyDevReloadParam, handleStaleEvent, type StaleEvent } from "./app/devStaleNotice";
// React Flow's base stylesheet, which every graph surface in the app needs and
// nothing here was loading.
//
// `@xyflow/react` ships positioning as CSS, not inline styles — most of all
// `.react-flow__viewport { transform-origin: 0 0 }`. Without it the viewport
// scales about its CENTRE, so every node is displaced by
// `(1 - zoom) x half the viewport box`. That error is proportional to how tall
// the arrangement is, which is why this hid for so long: compact graphs (a
// transaction, a 19-node case) looked very slightly off and nobody could name
// it, while the Transactions explorer's hierarchical layout — a 3150px-tall
// viewport — pushed every node ~1060px below the canvas and rendered a blank
// rectangle with 22 fully-formed nodes inside it.
//
// Every dev-host story and the marketing LiveDemo page imports this; the
// dashboard was the one host that never did, and `@authe/tx-graph`'s module
// docs name it as the host's job. Imported BEFORE ./index.css so Tailwind and
// ui-core still win where the two overlap.
import "@xyflow/react/dist/style.css";
import "./index.css";

/**
 * Keep the shared chain registry hydrated with the EFFECTIVE registry (bundled
 * catalogue ⊕ the published `manual:chain_registry`), in the background.
 * `/metadata-chains` is rewritten onto metadata-registry-api's `v0/chains` by
 * `RESOURCE_REWRITES`.
 *
 * Never awaited before render. It was, and it could not work: the client
 * resolves every URL from the routing token, which `SessionScopedLayers` sets
 * while the tree renders — so the pre-render request waited for a token that
 * could not arrive, timed out, and the tab kept the bundled catalogue for good.
 * Now it asks once the token is there (retrying 1/3/10/30/60 s) and re-reads
 * every 10 minutes, so a chain published in the admin portal reaches an open
 * tab. Chain pickers and avatars re-render on the registry version.
 */
function hydrateChains(apiClient: ApiClient) {
  return startEffectiveChainsHydration({
    url: "/metadata-chains",
    fetchJson: async (url, { signal }) =>
      unwrapData(await apiClient.get<unknown>(url, { signal, cacheTtlMs: 0, retries: 0 })),
    // The signed-in token the client routes by; null while signed out.
    isReady: () => getRoutingToken() !== null,
  });
}

async function bootstrap() {
  // App-wide explorer links (per chain) + hash/address truncation, applied to
  // the shared registries before anything renders.
  configureDisplayDefaults();

  // CloudWatch RUM (page loads, performance, JS errors, HTTP). No-op until the
  // RUM env ids are set. Separate from the ObservabilityProvider — see
  // ./app/observability for why.
  void initRum(appEnv);

  const manager = await createSessionManager();
  // When the gateway rejects a request because the token expired, force a fresh
  // token from Auth0 and push it into the synchronous routing-token snapshot the
  // router reads its bearer from (`__raw`), so the retried request carries it.
  // If the refresh fails (refresh token gone too), fall back to a re-login.
  // Every bearer handed out is tracked: blue/green routing follows the live token
  // (./app/routing-token), which is what the authorizer checks.
  const getAccessToken = trackBearer(() => manager.getAccessToken());
  const apiClient = createApiClient(
    getAccessToken,
    async () => {
      const fresh = await manager.refreshAccessToken();
      if (fresh) {
        const current = getRoutingToken();
        if (current) setRoutingToken({ ...current, __raw: fresh });
        return true;
      }
      await manager.login();
      return false;
    },
  );
  // Signed-cookie CDN delivery for cold transaction bodies (mints lazily).
  configureCdnForApp(apiClient);
  // ECDSA two-key device signing: registers the client so the api-client's
  // `signRequest` can lazily enrol the device root key (POST /device-keys) and
  // then attach `X-Auth-*` headers alongside REQUEST-KEY. Enrolment is
  // best-effort and never blocks a request.
  configureDeviceSigningForApp(apiClient);
  // HTTP GraphQL read client (fast path); null when PRIVATE_API_GRAPHQL_URL is
  // unset, in which case every data operation stays on REST.
  const graphqlClient = createDashboardGraphqlClient(getAccessToken);
  const queryClient = createQueryClient();
  // An identity change — sign-out, a tenant or org switch, a plan re-mint —
  // must not leave the departing identity's responses in memory. Most query
  // keys and every ApiClient cache key carry no tenancy (the key is the URL),
  // so a response cached for one tenant answers the same URL for the next if
  // anything reads it before the page navigates. Cheap to drop: the page is
  // about to leave or re-read everything anyway.
  manager.subscribeIdentityChange(() => {
    apiClient.invalidateMatching("");
    queryClient.clear();
  });

  // The effective chain registry, in the background — never before the first
  // paint (see `hydrateChains`). (The wagmi config in `./app/web3` is built at
  // import from the bundled registry, so the wallet's chain list does not
  // include chains published since the build.)
  const chains = hydrateChains(apiClient);

  const container = document.getElementById("root");
  if (!container) throw new Error("#root element not found");

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <AppProviders
        manager={manager}
        apiClient={apiClient}
        queryClient={queryClient}
        graphqlClient={graphqlClient}
      >
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );

  // Unmounting also disposes the identity-scoped streaming provider.
  import.meta.hot?.dispose(() => {
    chains.stop();
    root.unmount();
  });
  // Rebuilds of the linked packages arrive as one batched notice; the tab
  // reloads, or holds and says so when opened with `?devReload=hold`.
  if (import.meta.hot) {
    applyDevReloadParam();
    import.meta.hot.on("authe:stale", (event: StaleEvent) => handleStaleEvent(event));
  }
}

void bootstrap().catch((error: unknown) => {
  const container = document.getElementById("root");
  if (!container) return;
  const message = document.createElement("p");
  message.setAttribute("role", "alert");
  message.textContent = error instanceof Error ? error.message : "The dashboard could not start.";
  const retry = document.createElement("button");
  // An ended impersonation is the one failure a reload cannot fix: the marker
  // that produced it is durable (deliberately — see `clearImpersonation`), so
  // "Reload dashboard" came straight back here, for as long as the tab lived.
  // The way out is to stop being the customer, which is what this says.
  const ended = error instanceof ImpersonationEndedError;
  retry.textContent = ended ? "Continue as yourself" : "Reload dashboard";
  retry.onclick = () => (ended ? leaveEndedImpersonation() : window.location.reload());
  container.replaceChildren(message, retry);
});
